import {ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, inject} from '@angular/core';
import {CommonModule, DatePipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {
  Era5DayNormalizedWeatherService,
  Era5DayWeatherForecastService,
  Era5DayWeatherReportService,
  IbmDayWeatherForecastService
} from '@6grain/masofa-monolith-api-client/weather-report';
import {CheckboxComponent} from '@components/checkbox';
import {DateFormatLangPipe} from '@app/utils/pipes/date-format-lang-pipe';
import {MsfSelect} from '@components/select/select';
import {UgmWeatherDataService} from '@6grain/masofa-monolith-api-client/weather';
import {DatePickerComponent} from '@components/date-picker';

/**
 * Temperature tolerance for NORMAL range comparison (±3°C)
 */
export const TEMPERATURE_TOLERANCE = 3;

export interface WeatherInfo {
  temperature: number | null;
  temperatureMin: number | null;
  temperatureMax: number | null;
  humidity: number | null;
  windSpeed: number | null;
  windDirection: number | null;
  precipitation: number | null;
  solarRadiation: number | null;
  date: string | undefined;
  precipitationLevel?: string; // NORMAL, BELOW_NORMAL, ABOVE_NORMAL
  temperatureLevel?: string;
  solarRadiationLevel?: string;
  humidityLevel?: string;
  windSpeedLevel?: string;
  sumOfActiveTemperaturesBase5: number | null;
  sumOfActiveTemperaturesBase15: number | null;
  sumOfActiveTemperaturesBase10: number | null;
  sumOfActiveTemperaturesBase12: number | null;
  sumOfFallout: number | null;
  sumOfSolarRadiation: number | null;
  // Normalized values (norms)
  normFallout: number | null;
  normHumidity: number | null;
  normSolarRadiationInfluence: number | null;
  normTemperatureAverage: number | null;
  normTemperatureMax: number | null;
  normTemperatureMaxTotal: number | null;
  normTemperatureMin: number | null;
  normTemperatureMinTotal: number | null;
  normWeatherStation: string | null;
  normWindDirection: number | null;
  normWindSpeed: number | null;
}

@Component({
  selector: 'app-weather-info-tab',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    CheckboxComponent,
    MsfSelect,
    DatePickerComponent
  ],
  templateUrl: './weather-info-tab.component.html',
  styleUrls: ['./weather-info-tab.component.scss'],
  providers: [
    DateFormatLangPipe,
    DatePipe,
  ],
})
export class WeatherInfoTabComponent implements OnChanges {
  private era5DayWeatherForecastService = inject(Era5DayWeatherForecastService);
  private era5DayWeatherReportService = inject(Era5DayWeatherReportService);
  private era5DayWeatherNormalizedService = inject(Era5DayNormalizedWeatherService);
  private ugmWeatherDataService = inject(UgmWeatherDataService);
  private ibmDayWeatherForecastService = inject(IbmDayWeatherForecastService);
  public translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  public datePipe = inject(DatePipe);

  @Input() latitude: number | null = null;
  @Input() longitude: number | null = null;
  @Input() dataSourceOptions?: any[];
  @Input() date?: string;

  weatherInfo: WeatherInfo | null = null;
  isLoading: boolean = false;
  interpolationEnabled: boolean = false;
  selectedDataSource: string = 'era5';
  today = new Date();

  /**
   * Check if norms are available (at least one norm value is not null)
   */
  get hasNorms(): boolean {
    if (!this.weatherInfo) return false;
    return !!(
      this.weatherInfo.normFallout !== null ||
      this.weatherInfo.normHumidity !== null ||
      this.weatherInfo.normSolarRadiationInfluence !== null ||
      this.weatherInfo.normTemperatureAverage !== null ||
      this.weatherInfo.normWindSpeed !== null
    );
  }

  getTranslatedString(key: string, fallback: string): string {
    const translated = this.translate.instant(key);
    return translated !== key ? translated : fallback;
  }

  private normalizeSolarRadiation(value: number | null | undefined, source: string): number {
    if (value === null || value === undefined || value <= 0) {
      return 0;
    }

    // ERA5 values are already in kcal/cm²
    if (source === 'era5') {
      return Math.round(value);
    }

    // // UGM and IBM values are in Watts/m², convert to kcal/cm²
    // // Coefficient 42.4 converts Watts/m² to kcal/cm²
    // if (source === 'ugm' || source === 'ibm') {
    //   return Math.round(value / 42.4);
    // }

    // Default: return as is (should not happen)
    return Math.round(value);
  }

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['latitude'] || changes['longitude']) &&
       this.latitude !== null && this.longitude !== null) {
      this.loadWeatherInfo();
    }
  }

  /**
   * Load current weather information based on selected data source
   */
  public loadWeatherInfo(): void {
    if (this.latitude === null || this.longitude === null) return;

    this.isLoading = true;
    this.cdr.markForCheck();

    // Choose the appropriate data loading method based on data source
    switch (this.selectedDataSource.toLowerCase()) {
      case 'era5':
        this.loadEra5Data();
        break;
      case 'ugm':
        this.loadUgmData();
        break;
      case 'ibm':
        this.loadIbmData();
        break;
      default:
        this.loadEra5Data();
    }
  }

  private isTodayOrLater(date: Date): boolean {
      const today = new Date();
      
      // Обнуляем время у обеих дат
      const d1 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const d2 = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      return d2 >= d1;
  }
  /**
   * Load ERA5 weather data for today
   */
  private loadEra5Data(): void {
    const selectedDate = new Date(this.date as string);
    const dateStr = this.datePipe.transform(selectedDate, 'yyyy-MM-dd') as string;

    var request:any;

    if(this.isTodayOrLater(selectedDate)) {
      request = this.interpolationEnabled
        ? this.era5DayWeatherForecastService.weatherReportsEra5DayWeatherForecastGetInterpolatedByCoordinatesAndDatePost({
            latitude: this.latitude!,
            longitude: this.longitude!,
            inputDate: dateStr
          })
        : this.era5DayWeatherForecastService.weatherReportsEra5DayWeatherForecastGetByCoordinatesAndDatePost({
            latitude: this.latitude!,
            longitude: this.longitude!,
            inputDate: dateStr
          });
    }
    else
    {
      request = this.era5DayWeatherReportService.weatherReportsEra5DayWeatherReportGetByCoordinatesAndDatePost({
        latitude: this.latitude!,
          longitude: this.longitude!,
          inputDate: dateStr
      });
    }

    const normalsRequest = this.interpolationEnabled
      ? this.era5DayWeatherNormalizedService.weatherReportsEra5DayNormalizedWeatherGetInterpolatedByCoordinatesAndDatePost({
          latitude: this.latitude!,
          longitude: this.longitude!,
          inputDate: dateStr
        })
      : this.era5DayWeatherNormalizedService.weatherReportsEra5DayNormalizedWeatherGetByCoordinatesAndDatePost({
          latitude: this.latitude!,
          longitude: this.longitude!,
          inputDate: dateStr
        });

    this.isLoading = true;

    request.pipe(
      map((data:any) => {
        if (!data) return null;
        const solarRadiation = data.solarRadiationInfluence ?? 0;
        const processedSolarRadiation = this.normalizeSolarRadiation(solarRadiation, 'era5');
        return {
          temperature: data.temperatureAverage !== null && data.temperatureAverage !== undefined
            ? this.roundToDecimal(data.temperatureAverage, 2) : null,
          temperatureMin: data.temperatureMin !== null && data.temperatureMin !== undefined
            ? this.roundToDecimal(data.temperatureMin, 2) : null,
          temperatureMax: data.temperatureMax !== null && data.temperatureMax !== undefined
            ? this.roundToDecimal(data.temperatureMax, 2) : null,
          humidity: data.humidity !== null && data.humidity !== undefined
            ? this.roundToDecimal(data.humidity, 2) : null,
          windSpeed: data.windSpeed !== null && data.windSpeed !== undefined
            ? this.roundToDecimal(data.windSpeed, 2) : null,
          windDirection: data.windDerection !== null && data.windDerection !== undefined
            ? this.roundToDecimal(data.windDerection, 2) : null,
          precipitation: data.fallout !== null && data.fallout !== undefined
            ? this.roundToDecimal(data.fallout, 2) : null,
          solarRadiation: processedSolarRadiation,
          date: data.date as any,
          sumOfActiveTemperaturesBase5: data.sumOfActiveTemperaturesBase5 !== null && data.sumOfActiveTemperaturesBase5 !== undefined
            ? this.roundToDecimal(data.sumOfActiveTemperaturesBase5, 2) : null,
          sumOfActiveTemperaturesBase15: data.sumOfActiveTemperaturesBase15 !== null && data.sumOfActiveTemperaturesBase15 !== undefined
            ? this.roundToDecimal(data.sumOfActiveTemperaturesBase15, 2) : null,
          sumOfActiveTemperaturesBase10: data.sumOfActiveTemperaturesBase10 !== null && data.sumOfActiveTemperaturesBase10 !== undefined
            ? this.roundToDecimal(data.sumOfActiveTemperaturesBase10, 2) : null,
          sumOfActiveTemperaturesBase12: data.sumOfActiveTemperaturesBase12 !== null && data.sumOfActiveTemperaturesBase12 !== undefined
            ? this.roundToDecimal(data.sumOfActiveTemperaturesBase12, 2) : null,
          sumOfFallout: data.sumOfFallout !== null && data.sumOfFallout !== undefined
            ? this.roundToDecimal(data.sumOfFallout, 2) : null,
          sumOfSolarRadiation: data.sumOfSolarRadiation !== null && data.sumOfSolarRadiation !== undefined
            ? this.roundToDecimal(data.sumOfSolarRadiation, 2) : null
        };
      }),
      catchError(error => {
        console.error('Error loading ERA5 weather info:', error);
        this.isLoading = false;
        this.weatherInfo = null;
        this.cdr.markForCheck();
        return of(null);
      })
    ).subscribe((weatherData: any) => {
      if (!weatherData) {
        this.isLoading = false;
        this.cdr.markForCheck();
        return;
      }
      normalsRequest.pipe(
        map(normals => {
          // Check if norms are actually available (not null/undefined)
          // Handle case when normals is null or undefined
          if (!normals) {
            // If interpolation is enabled and no norms, return undefined levels
            const levelsWhenNoNorms = this.interpolationEnabled ? {
              precipitationLevel: undefined,
              temperatureLevel: undefined,
              solarRadiationLevel: undefined,
              humidityLevel: undefined,
              windSpeedLevel: undefined
            } : {
              precipitationLevel: 'NORMAL' as const,
              temperatureLevel: 'NORMAL' as const,
              solarRadiationLevel: 'NORMAL' as const,
              humidityLevel: 'NORMAL' as const,
              windSpeedLevel: 'NORMAL' as const
            };

            return {
              ...weatherData,
              ...levelsWhenNoNorms,
              normFallout: null,
              normHumidity: null,
              normSolarRadiationInfluence: null,
              normTemperatureAverage: null,
              normTemperatureMax: null,
              normTemperatureMaxTotal: null,
              normTemperatureMin: null,
              normTemperatureMinTotal: null,
              normWeatherStation: null,
              normWindDirection: null,
              normWindSpeed: null
            };
          }

          function getLevel(value: number | null, normal: number | null): string | undefined {
            // If no norm available, return undefined instead of 'NORMAL' to indicate missing data
            if (normal === null || normal === undefined) return undefined;
            if (value === null || value === undefined) return undefined;
            if (value < normal) return 'BELOW_NORMAL';
            if (value > normal) return 'ABOVE_NORMAL';
            return 'NORMAL';
          }

          // Temperature uses ±3 degree tolerance for NORMAL range
          function getTemperatureLevel(value: number | null, normal: number | null): string | undefined {
            if (normal === null || normal === undefined) return undefined;
            if (value === null || value === undefined) return undefined;
            if (value < normal - TEMPERATURE_TOLERANCE) return 'BELOW_NORMAL';
            if (value > normal + TEMPERATURE_TOLERANCE) return 'ABOVE_NORMAL';
            return 'NORMAL';
          }

          return {
            ...weatherData,
            precipitationLevel: getLevel(weatherData.precipitation, normals?.fallout ?? null),
            temperatureLevel: getTemperatureLevel(weatherData.temperature, normals?.temperatureAverage ?? null),
            solarRadiationLevel: getLevel(weatherData.solarRadiation, normals?.solarRadiationInfluence ?? null),
            humidityLevel: getLevel(weatherData.humidity, normals?.humidity ?? null),
            windSpeedLevel: getLevel(weatherData.windSpeed, normals?.windSpeed ?? null),
            // Add normalized values
            normFallout: normals?.fallout !== null && normals?.fallout !== undefined
              ? this.roundToDecimal(normals.fallout, 2) : null,
            normHumidity: normals?.humidity !== null && normals?.humidity !== undefined
              ? this.roundToDecimal(normals.humidity, 2) : null,
            normSolarRadiationInfluence: normals?.solarRadiationInfluence !== null && normals?.solarRadiationInfluence !== undefined
              ? this.roundToDecimal(normals.solarRadiationInfluence, 2) : null,
            normTemperatureAverage: normals?.temperatureAverage !== null && normals?.temperatureAverage !== undefined
              ? this.roundToDecimal(normals.temperatureAverage, 2) : null,
            normTemperatureMax: normals?.temperatureMax !== null && normals?.temperatureMax !== undefined
              ? this.roundToDecimal(normals.temperatureMax, 2) : null,
            normTemperatureMaxTotal: normals?.temperatureMaxTotal !== null && normals?.temperatureMaxTotal !== undefined
              ? this.roundToDecimal(normals.temperatureMaxTotal, 2) : null,
            normTemperatureMin: normals?.temperatureMin !== null && normals?.temperatureMin !== undefined
              ? this.roundToDecimal(normals.temperatureMin, 2) : null,
            normTemperatureMinTotal: normals?.temperatureMinTotal !== null && normals?.temperatureMinTotal !== undefined
              ? this.roundToDecimal(normals.temperatureMinTotal, 2) : null,
            normWeatherStation: normals?.weatherStation ?? null,
            normWindDirection: normals?.windDerection !== null && normals?.windDerection !== undefined
              ? this.roundToDecimal(normals.windDerection, 2) : null,
            normWindSpeed: normals?.windSpeed !== null && normals?.windSpeed !== undefined
              ? this.roundToDecimal(normals.windSpeed, 2) : null
          };
        }),
        catchError(error => {
          console.error('Error loading ERA5 normals:', error);
          // When interpolation is enabled and norms fail to load, set levels to undefined (not 'NORMAL')
          // to indicate that norms are unavailable
          const levelsWhenNoNorms = this.interpolationEnabled ? {
            precipitationLevel: undefined,
            temperatureLevel: undefined,
            solarRadiationLevel: undefined,
            humidityLevel: undefined,
            windSpeedLevel: undefined
          } : {
            precipitationLevel: 'NORMAL' as const,
            temperatureLevel: 'NORMAL' as const,
            solarRadiationLevel: 'NORMAL' as const,
            humidityLevel: 'NORMAL' as const,
            windSpeedLevel: 'NORMAL' as const
          };

          return of({
            ...weatherData,
            ...levelsWhenNoNorms,
            normFallout: null,
            normHumidity: null,
            normSolarRadiationInfluence: null,
            normTemperatureAverage: null,
            normTemperatureMax: null,
            normTemperatureMaxTotal: null,
            normTemperatureMin: null,
            normTemperatureMinTotal: null,
            normWeatherStation: null,
            normWindDirection: null,
            normWindSpeed: null
          });
        })
      ).subscribe(finalData => {

        this.weatherInfo = finalData;
        this.isLoading = false;
        this.cdr.markForCheck();
      });
    });
  }

  /**
   * Load UGM weather data for today
   */
  private loadUgmData(): void {
    const today = new Date(this.date as string);
    const dateStr = this.datePipe.transform(today, 'yyyy-MM-dd') as string // Format: YYYY-MM-DD

    this.ugmWeatherDataService.weatherUgmWeatherDataGetByCoordinatesAndDatePost({
      latitude: this.latitude!,
      longitude: this.longitude!,
      inputDate: dateStr
    }).pipe(
      map((data: any) => {
        if (!data) {
          return null;
        }

        // UGM API has different property names, use 'any' type to access them
        const tempMin = data.temperatureMin ?? null;
        const tempMax = data.temperatureMax ?? null;
        const tempAvg = (tempMin !== null && tempMax !== null)
          ? (tempMin + tempMax) / 2
          : null;

        // Process solar radiation if available
        const solarRadiation = data.solarRadiation ?? 0;
        const processedSolarRadiation = this.normalizeSolarRadiation(solarRadiation, 'ugm');

        return {
          temperature: tempAvg !== null ? this.roundToDecimal(tempAvg, 2) : null,
          temperatureMin: tempMin !== null ? this.roundToDecimal(tempMin, 2) : null,
          temperatureMax: tempMax !== null ? this.roundToDecimal(tempMax, 2) : null,
          humidity: data.humidity !== null && data.humidity !== undefined
            ? this.roundToDecimal(data.humidity, 2) : null,
          windSpeed: data.windSpeedMin !== null && data.windSpeedMin !== undefined
            ? this.roundToDecimal(data.windSpeedMin, 2) : null,
          windDirection: data.windDirection !== null && data.windDirection !== undefined
            ? this.roundToDecimal(data.windDirection, 2) : null,
          precipitation: data.precipitation !== null && data.precipitation !== undefined
            ? this.roundToDecimal(data.precipitation, 2) : null,
          solarRadiation: processedSolarRadiation,
          date: data.date as any,
          sumOfActiveTemperaturesBase5: null,
          sumOfActiveTemperaturesBase15: null,
          sumOfActiveTemperaturesBase10: null,
          sumOfActiveTemperaturesBase12: null,
          sumOfFallout: null,
          sumOfSolarRadiation: null,
          // Normalized values (not available for UGM)
          normFallout: null,
          normHumidity: null,
          normSolarRadiationInfluence: null,
          normTemperatureAverage: null,
          normTemperatureMax: null,
          normTemperatureMaxTotal: null,
          normTemperatureMin: null,
          normTemperatureMinTotal: null,
          normWeatherStation: null,
          normWindDirection: null,
          normWindSpeed: null
        };
      }),
      catchError(error => {
        this.isLoading = false;
        this.weatherInfo = null
        this.cdr.markForCheck();
        return of(null);
      })
    ).subscribe(weatherData => {
      this.isLoading = false;
      if (weatherData) {
        this.weatherInfo = weatherData;
      }
      this.cdr.markForCheck();
    });
  }

  /**
   * Load IBM weather data for today
   */
  private loadIbmData(): void {
    const today = new Date(this.date as string);
    const dateStr = this.datePipe.transform(today, 'yyyy-MM-dd') as string // Format: YYYY-MM-DD

    // Choose the appropriate API method based on interpolation checkbox
    const request = this.interpolationEnabled
      ? this.ibmDayWeatherForecastService.weatherReportsIbmDayWeatherForecastGetInterpolatedByCoordinatesAndDatePost({
          latitude: this.latitude!,
          longitude: this.longitude!,
          inputDate: dateStr
        })
      : this.ibmDayWeatherForecastService.weatherReportsIbmDayWeatherForecastGetByCoordinatesAndDatePost({
          latitude: this.latitude!,
          longitude: this.longitude!,
          inputDate: dateStr
        });

    const normalsRequest = this.interpolationEnabled
      ? this.era5DayWeatherNormalizedService.weatherReportsEra5DayNormalizedWeatherGetInterpolatedByCoordinatesAndDatePost({
          latitude: this.latitude!,
          longitude: this.longitude!,
          inputDate: dateStr
        })
      : this.era5DayWeatherNormalizedService.weatherReportsEra5DayNormalizedWeatherGetByCoordinatesAndDatePost({
          latitude: this.latitude!,
          longitude: this.longitude!,
          inputDate: dateStr
        });

    request.pipe(
      map((data: any) => {
        if (!data) {
          return null;
        }

        // IBM API has different property names, use 'any' type to access them
        const tempMin = data.temperatureMin ?? null;
        const tempMax = data.temperatureMax ?? null;
        const tempAvg = (tempMin !== null && tempMax !== null)
          ? (tempMin + tempMax) / 2
          : null;

        // Process solar radiation if available (IBM may not have this field)
        const solarRadiation = data.solarRadiation ?? 0;
        const processedSolarRadiation = this.normalizeSolarRadiation(solarRadiation, 'ibm');

        return {
          temperature: tempAvg !== null ? this.roundToDecimal(tempAvg, 2) : null,
          temperatureMin: tempMin !== null ? this.roundToDecimal(tempMin, 2) : null,
          temperatureMax: tempMax !== null ? this.roundToDecimal(tempMax, 2) : null,
          humidity: data.humidity !== null && data.humidity !== undefined
            ? this.roundToDecimal(data.humidity, 2) : null,
          windSpeed: data.windSpeed !== null && data.windSpeed !== undefined
            ? this.roundToDecimal(data.windSpeed, 2) : null,
          windDirection: data.windDerection !== null && data.windDerection !== undefined
            ? this.roundToDecimal(data.windDerection, 2) : null,
          precipitation: data.fallout !== null && data.fallout !== undefined
            ? this.roundToDecimal(data.fallout, 2) : null,
          solarRadiation: processedSolarRadiation,
          date: data.date as any,
          sumOfActiveTemperaturesBase5:  data.sumOfActiveTemperaturesBase5 !== null && data.sumOfActiveTemperaturesBase5 !== undefined
            ? this.roundToDecimal(data.sumOfActiveTemperaturesBase5, 2) : null,
          sumOfActiveTemperaturesBase15: data.sumOfActiveTemperaturesBase15 !== null && data.sumOfActiveTemperaturesBase15 !== undefined
            ? this.roundToDecimal(data.sumOfActiveTemperaturesBase15, 2) : null,
          sumOfActiveTemperaturesBase10: data.sumOfActiveTemperaturesBase10 !== null && data.sumOfActiveTemperaturesBase10 !== undefined
            ? this.roundToDecimal(data.sumOfActiveTemperaturesBase10, 2) : null,
          sumOfActiveTemperaturesBase12: data.sumOfActiveTemperaturesBase12 !== null && data.sumOfActiveTemperaturesBase12 !== undefined
            ? this.roundToDecimal(data.sumOfActiveTemperaturesBase12, 2) : null,
          sumOfFallout: data.sumOfFallout !== null && data.sumOfFallout !== undefined
            ? this.roundToDecimal(data.sumOfFallout, 2) : null,
          sumOfSolarRadiation: data.sumOfSolarRadiation !== null && data.sumOfSolarRadiation !== undefined
            ? this.roundToDecimal(data.sumOfSolarRadiation, 2) : null,
          // Normalized values (not available for IBM)
          normFallout: null,
          normHumidity: null,
          normSolarRadiationInfluence: null,
          normTemperatureAverage: null,
          normTemperatureMax: null,
          normTemperatureMaxTotal: null,
          normTemperatureMin: null,
          normTemperatureMinTotal: null,
          normWeatherStation: null,
          normWindDirection: null,
          normWindSpeed: null
        };
      }),
      catchError(error => {
        console.error('Error loading IBM weather info:', error);
        this.isLoading = false;
        this.weatherInfo = null
        this.cdr.markForCheck();
        return of(null);
      })
    ).subscribe(weatherData => {
      if (!weatherData) {
        this.isLoading = false;
        this.cdr.markForCheck();
        return;
      }

      normalsRequest.pipe(
        map(normals => {
          function getLevel(value: number | null, normal: number | null): string | undefined {
            if (normal === null || normal === undefined) return undefined;
            if (value === null || value === undefined) return undefined;
            if (value < normal) return 'BELOW_NORMAL';
            if (value > normal) return 'ABOVE_NORMAL';
            return 'NORMAL';
          }

          function getTemperatureLevel(value: number | null, normal: number | null): string | undefined {
            if (normal === null || normal === undefined) return undefined;
            if (value === null || value === undefined) return undefined;
            if (value < normal - TEMPERATURE_TOLERANCE) return 'BELOW_NORMAL';
            if (value > normal + TEMPERATURE_TOLERANCE) return 'ABOVE_NORMAL';
            return 'NORMAL';
          }

          if (!normals) {
            return {
              ...weatherData,
              precipitationLevel: undefined,
              temperatureLevel: undefined,
              solarRadiationLevel: undefined,
              humidityLevel: undefined,
              windSpeedLevel: undefined,
              normFallout: null,
              normHumidity: null,
              normSolarRadiationInfluence: null,
              normTemperatureAverage: null,
              normTemperatureMax: null,
              normTemperatureMaxTotal: null,
              normTemperatureMin: null,
              normTemperatureMinTotal: null,
              normWeatherStation: null,
              normWindDirection: null,
              normWindSpeed: null
            };
          }

          return {
            ...weatherData,
            precipitationLevel: getLevel(weatherData.precipitation, normals?.fallout ?? null),
            temperatureLevel: getTemperatureLevel(weatherData.temperature, normals?.temperatureAverage ?? null),
            solarRadiationLevel: getLevel(weatherData.solarRadiation, normals?.solarRadiationInfluence ?? null),
            humidityLevel: getLevel(weatherData.humidity, normals?.humidity ?? null),
            windSpeedLevel: getLevel(weatherData.windSpeed, normals?.windSpeed ?? null),
            normFallout: normals?.fallout !== null && normals?.fallout !== undefined
              ? this.roundToDecimal(normals.fallout, 2) : null,
            normHumidity: normals?.humidity !== null && normals?.humidity !== undefined
              ? this.roundToDecimal(normals.humidity, 2) : null,
            normSolarRadiationInfluence: normals?.solarRadiationInfluence !== null && normals?.solarRadiationInfluence !== undefined
              ? this.roundToDecimal(normals.solarRadiationInfluence, 2) : null,
            normTemperatureAverage: normals?.temperatureAverage !== null && normals?.temperatureAverage !== undefined
              ? this.roundToDecimal(normals.temperatureAverage, 2) : null,
            normTemperatureMax: normals?.temperatureMax !== null && normals?.temperatureMax !== undefined
              ? this.roundToDecimal(normals.temperatureMax, 2) : null,
            normTemperatureMaxTotal: normals?.temperatureMaxTotal !== null && normals?.temperatureMaxTotal !== undefined
              ? this.roundToDecimal(normals.temperatureMaxTotal, 2) : null,
            normTemperatureMin: normals?.temperatureMin !== null && normals?.temperatureMin !== undefined
              ? this.roundToDecimal(normals.temperatureMin, 2) : null,
            normTemperatureMinTotal: normals?.temperatureMinTotal !== null && normals?.temperatureMinTotal !== undefined
              ? this.roundToDecimal(normals.temperatureMinTotal, 2) : null,
            normWeatherStation: normals?.weatherStation ?? null,
            normWindDirection: normals?.windDerection !== null && normals?.windDerection !== undefined
              ? this.roundToDecimal(normals.windDerection, 2) : null,
            normWindSpeed: normals?.windSpeed !== null && normals?.windSpeed !== undefined
              ? this.roundToDecimal(normals.windSpeed, 2) : null
          };
        }),
        catchError(error => {
          console.error('Error loading ERA5 normals for IBM source:', error);
          return of({
            ...weatherData,
            precipitationLevel: undefined,
            temperatureLevel: undefined,
            solarRadiationLevel: undefined,
            humidityLevel: undefined,
            windSpeedLevel: undefined,
            normFallout: null,
            normHumidity: null,
            normSolarRadiationInfluence: null,
            normTemperatureAverage: null,
            normTemperatureMax: null,
            normTemperatureMaxTotal: null,
            normTemperatureMin: null,
            normTemperatureMinTotal: null,
            normWeatherStation: null,
            normWindDirection: null,
            normWindSpeed: null
          });
        })
      ).subscribe(finalData => {
        this.weatherInfo = finalData;
        this.isLoading = false;
        this.cdr.markForCheck();
      });
    });
  }

  /**
   * Round number to specified decimal places
   */
  private roundToDecimal(value: number, decimals: number): number {
    const multiplier = Math.pow(10, decimals);
    return Math.round(value * multiplier) / multiplier;
  }

  /**
   * Called when interpolation checkbox changes
   */
  onInterpolationChange(): void {
    if (this.latitude !== null && this.longitude !== null) {
      this.loadWeatherInfo();
    }
  }

  /**
   * Called when data source changes
   */
  onDataSourceChange(): void {
    if (this.latitude !== null && this.longitude !== null) {
      this.loadWeatherInfo();
    }
  }

  /**
   * Format date for display
   */

  getCardinalDirection(degrees: number): string {
    const directions = [
      'N',
      'NE',
      'E',
      'SE',
      'S',
      'SW',
      'W',
      'NW'
    ];
    const index = Math.round(((degrees % 360) / 45)) % 8;
    return directions[index];
  }

  getWindArrowRotation(degrees: number | null | undefined): string {
    if (degrees === null || degrees === undefined) return 'rotate(0deg)';
    return `rotate(${degrees}deg)`;
  }

  getWeatherIconPath(iconName: string): string {
    return `/assets/icons/main/light/${iconName}.svg`;
  }


  getLevelIcon(level: string | undefined): string {
    switch (level) {
      case 'NORMAL':
        return this.getWeatherIconPath('norm-point');
      case 'BELOW_NORMAL':
        return this.getWeatherIconPath('sun-lights');
      case 'ABOVE_NORMAL':
        return this.getWeatherIconPath('stop');
      default:
        return ''
    }
  }
}
